import classNames from 'classnames';
import { useState, useCallback, useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import Markdown from 'react-markdown';

import { useSubscription } from '../../SubscriptionProvider';
import { createSubscriptionCheckoutSession } from '../../../../utils/service';
import { getGenericError } from '../../../../utils/errors';
import CouponCodeInput from '../../../../global/CouponCodeInput';
import StripeCustomCheckout from '../../../../global/StripeElements/StripeCustomCheckout';
import { SUBSCRIPTION_STEPS, getIntervalTag } from '../../../../utils/subscriptions';
import { SUBSCRIPTION_TRACKER_TYPES, TRACKER_IDS, getTrackerIdEventName } from '../../../../config/tracker';
import { MEMBERSHIP_TERMS_URL } from '../../../../config/externalURL';
import { ROUTE_HOME } from '../../../../config/routes';
import { useSubscriptionStepScroll } from '../../../../utils/subscriptionFlow';
import { pushSubscriptionStartedEvent } from '../../../../utils/gtm';
import { useFirebaseAuth } from '../../../../../global/FirebaseProvider/FirebaseProvider';
import { insertTCIcon, TBB_TC_ICON } from '../../../../utils/tbbRegistration';
import { formatDecimalNumber } from '../../../../utils/numbers';
import CheckCircleIcon from '../../../../images/check-circle-icon.svg';
import {
  SubscriptionHero,
  SubscriptionStepCard,
  SubscriptionLegal,
} from '../../shared';

import './SubscriptionDirectPayment.scss';

const SLIDE_SUMMARY = 1;
const SLIDE_COUPON_TERMS = 2;
const SLIDE_STRIPE = 3;

const {
  subscription: {
    payment: {
      [SUBSCRIPTION_TRACKER_TYPES.VIEW]: trackerView,
      [SUBSCRIPTION_TRACKER_TYPES.CLOSE]: trackerClose,
    },
  },
} = TRACKER_IDS;

const SubscriptionDirectPayment = () => {
  const hasTrackedInitiateCheckout = useRef(false);
  const history = useHistory();
  const { userData } = useFirebaseAuth();
  const {
    config: subscriptionConfig,
    subscriptionPlan,
    posthog,
    isMobileSize,
    searchParams,
  } = useSubscription();

  const [currentSlide, setCurrentSlide] = useState(SLIDE_SUMMARY);
  const slideFlowAnimRef = useRef({ slide: currentSlide, direction: 'forward' });
  if (slideFlowAnimRef.current.slide !== currentSlide) {
    const prevSlide = slideFlowAnimRef.current.slide;
    slideFlowAnimRef.current = {
      slide: currentSlide,
      direction: currentSlide < prevSlide ? 'back' : 'forward',
    };
  }
  const slideFlowDirection = slideFlowAnimRef.current.direction;
  const directPaymentStepSectionRef = useRef(null);
  useSubscriptionStepScroll(directPaymentStepSectionRef, currentSlide);
  const [checkoutEnabled, setCheckoutEnabled] = useState(true);
  const [errorStripe, setErrorStripe] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [isLoadingClientSecret, setIsLoadingClientSecret] = useState(false);
  const [couponMessage, setCouponMessage] = useState('');
  const [couponErrorMessage, setCouponErrorMessage] = useState('');
  const [couponCode, setCouponCode] = useState('');
  const [appliedCouponCode, setAppliedCouponCode] = useState('');
  const [discountedValue, setDiscountedValue] = useState(null);
  const [isCouponApplied, setIsCouponApplied] = useState(false);

  const {
    bonusText,
    offerSavingsText,
    items = [],
    terms,
  } = subscriptionConfig?.steps?.[SUBSCRIPTION_STEPS.DIRECT_PAYMENT] || {};
  const info = subscriptionConfig?.steps?.[SUBSCRIPTION_STEPS.PAYMENT]?.info || '';

  const {
    value: subscriptionValue = 0,
    prevValue: subscriptionPrevValue,
    interval: subscriptionInterval,
    tcEarnedForSubscribing,
  } = subscriptionPlan ?? {};

  const savings = Math.max(subscriptionPrevValue - subscriptionValue, 0);

  const fetchClientSecret = useCallback(async (couponId = null) => {
    setIsLoadingClientSecret(true);
    setCouponMessage('');
    setCouponErrorMessage('');

    const {
      data,
      error: createCheckoutSessionError,
    } = await createSubscriptionCheckoutSession({
      couponId,
      isDirectPayment: true,
      searchParams,
    });

    if (createCheckoutSessionError || !data?.clientSecret) {
      setErrorStripe(getGenericError());
      setClientSecret(null);
      setCheckoutEnabled(false);
      setIsLoadingClientSecret(false);
      return;
    }

    const appliedCoupon = Boolean(couponId && data.couponApplied);

    setClientSecret(data.clientSecret);
    setErrorStripe(null);
    setIsCouponApplied(appliedCoupon);
    setDiscountedValue(data.discountedValue);

    if (couponId) {
      if (appliedCoupon) {
        setCouponMessage('Coupon applied successfully');
        setCouponErrorMessage('');
      } else {
        setCouponMessage('');
        setCouponErrorMessage('Invalid coupon');
      }
    }

    setCouponCode('');
    setCheckoutEnabled(true);
    setIsLoadingClientSecret(false);
    if (!hasTrackedInitiateCheckout.current) {
      window.fbq?.('track', 'InitiateCheckout');
      hasTrackedInitiateCheckout.current = true;
    }
  }, []);

  const handleClose = useCallback(() => {
    posthog.capture(getTrackerIdEventName(trackerClose));
    history.push(ROUTE_HOME);
  }, [posthog, history]);

  const handleBack = useCallback(() => {
    if (currentSlide === SLIDE_STRIPE) {
      setCurrentSlide(SLIDE_COUPON_TERMS);
    } else if (currentSlide === SLIDE_COUPON_TERMS) {
      setCurrentSlide(SLIDE_SUMMARY);
    } else {
      handleClose();
    }
  }, [currentSlide]);

  useEffect(() => {
    fetchClientSecret(appliedCouponCode);
  }, [appliedCouponCode]);

  useEffect(() => {
    posthog.capture(getTrackerIdEventName(trackerView));
    // Push GTM subscription_started event
    pushSubscriptionStartedEvent(userData);
  }, []);

  const slideTitle = {
    [SLIDE_SUMMARY]: 'Your membership',
    [SLIDE_COUPON_TERMS]: 'Review & Checkout',
    [SLIDE_STRIPE]: 'Payment',
  };
  const slideDescription = {
    [SLIDE_SUMMARY]: 'All Trashie Unlimited members get a three-pack and a tech box to kick-off their membership.',
    [SLIDE_COUPON_TERMS]: 'Add a coupon if you have one, then continue to payment.',
    [SLIDE_STRIPE]: 'Complete your purchase securely below.',
  };

  return (
    <div className="SubscriptionDirectPayment">
      <SubscriptionHero />
      <div ref={directPaymentStepSectionRef} className="SubscriptionDirectPayment__content">
        <div
          key={currentSlide}
          className={classNames(
            'SubscriptionFlowPanel',
            slideFlowDirection === 'back' ? 'SubscriptionFlowPanel--back' : 'SubscriptionFlowPanel--forward',
          )}
        >
          <SubscriptionStepCard
            stepNumber={String(currentSlide)}
            title={slideTitle[currentSlide]}
            description={slideDescription[currentSlide]}
            onBack={handleBack}
          >
            {currentSlide === SLIDE_SUMMARY && (
            <div className="SubscriptionDirectPayment__section">
              <div className="SubscriptionDirectPayment__section-summary">
                <div className="SubscriptionDirectPayment__section-summary-label">
                  Trashie Unlimited
                  {' '}
                  <span className="SubscriptionDirectPayment__section-summary-label-line2">
                    Annual Membership
                  </span>
                </div>
                <div className="SubscriptionDirectPayment__section-summary-value">
                  {!!subscriptionPrevValue && (
                    <span className="prev-value">{`$${subscriptionPrevValue}`}</span>
                  )}
                  <span className="value">
                    {`$${subscriptionValue}`}
                    {getIntervalTag(subscriptionInterval)}
                  </span>
                </div>
                {tcEarnedForSubscribing > 0 && (
                  <div className="SubscriptionDirectPayment__earned">
                    {insertTCIcon(
                      `${TBB_TC_ICON}${formatDecimalNumber(tcEarnedForSubscribing)} ${bonusText}`,
                      isMobileSize ? 16 : 20,
                      'var(--trashie-ui-crisp-white)',
                    )}
                  </div>
                )}
              </div>
              <div className="SubscriptionDirectPayment__section-content">
                <div className="SubscriptionDirectPayment__section-content-items">
                  {items.map((item) => (
                    <div
                      className="SubscriptionDirectPayment__section-content-item"
                      key={item}
                    >
                      <img src={CheckCircleIcon} alt="check" />
                      <span className="SubscriptionDirectPayment__section-content-item-title">
                        {item}
                      </span>
                    </div>
                  ))}
                </div>
                {savings > 0 && (
                  <div className="SubscriptionDirectPayment__savings">
                    {`${offerSavingsText} $${savings}`}
                  </div>
                )}
              </div>
              <button
                type="button"
                className="SubscriptionDirectPayment__continue"
                onClick={() => setCurrentSlide(SLIDE_COUPON_TERMS)}
              >
                Continue
              </button>
            </div>
            )}

            {currentSlide === SLIDE_COUPON_TERMS && (
            <>
              <div className="SubscriptionDirectPayment__section">
                <CouponCodeInput
                  containerClassName="SubscriptionDirectPayment__section-coupon"
                  value={couponCode}
                  onChange={setCouponCode}
                  onApply={() => setAppliedCouponCode(couponCode)}
                  disabled={isLoadingClientSecret}
                  applyDisabled={!couponCode || isLoadingClientSecret}
                  successMessage={couponMessage}
                  errorMessage={couponErrorMessage}
                  isCompact
                />
                {errorStripe && (
                  <div className="SubscriptionDirectPayment__section-error">
                    {errorStripe}
                  </div>
                )}
              </div>
              {terms && (
                <div className="SubscriptionDirectPayment__inner-terms">
                  <Markdown>{terms}</Markdown>
                  <p>
                    To learn more,
                    &nbsp;
                    <a
                      href={MEMBERSHIP_TERMS_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="View our Terms of service"
                    >
                      view our Terms of service
                    </a>
                    .
                  </p>
                </div>
              )}
              <button
                type="button"
                className="SubscriptionDirectPayment__continue"
                onClick={() => setCurrentSlide(SLIDE_STRIPE)}
                disabled={!clientSecret && isLoadingClientSecret}
              >
                {!clientSecret && isLoadingClientSecret ? 'Loading…' : 'Continue to payment'}
              </button>
            </>
            )}

            {currentSlide === SLIDE_STRIPE && (
            <>
              <div className="SubscriptionDirectPayment__section SubscriptionDirectPayment__section--stripe">
                {!clientSecret && isLoadingClientSecret && (
                  <div className="SubscriptionDirectPayment__section-loading">
                    Loading checkout…
                  </div>
                )}
                {!isLoadingClientSecret && clientSecret && !!subscriptionValue && (
                  <StripeCustomCheckout
                    clientSecret={clientSecret}
                    fullValue={subscriptionValue}
                    discountedValue={discountedValue}
                    isCouponApplied={isCouponApplied}
                    disabled={!checkoutEnabled}
                    priceClassName="SubscriptionDirectPayment__embeddedCheckout-price"
                    fullValueClassName="SubscriptionDirectPayment__embeddedCheckout-price-fullValue"
                    discountedValueClassName="SubscriptionDirectPayment__embeddedCheckout-price-discountedValue"
                  />
                )}
              </div>
              <div className="SubscriptionDirectPayment__terms">
                {info && <Markdown>{info}</Markdown>}
                By completing this purchase, you agree to our
                &nbsp;
                <a
                  href={MEMBERSHIP_TERMS_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="View terms of service"
                >
                  Terms of service
                </a>
                .
              </div>
            </>
            )}
          </SubscriptionStepCard>
        </div>
      </div>
      <SubscriptionLegal />
    </div>
  );
};

export default SubscriptionDirectPayment;
